import {
  type CancelForSupplyFailureInput,
  orderReasonSchema,
  type RefundOrderCancelInput,
} from '@repo/api-contracts';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { type OrderRefundResult, refundResultMessage } from '@/shared/lib';
import { formStyles, Label, OrderActionDialog } from '@/shared/ui';

interface OrderRefundDialogProps {
  readonly orderId: string;
  readonly hasRequest: boolean;
  readonly supplyFailure?: boolean;
  readonly onSubmit: (input: RefundOrderCancelInput) => Promise<OrderRefundResult>;
  readonly onSupplySubmit: (input: CancelForSupplyFailureInput) => Promise<OrderRefundResult>;
  readonly onReload: () => void;
  readonly onClose: () => void;
  readonly pending?: boolean;
  readonly paymentId?: string;
}

/**
 * 취소·환불(또는 공급 불가 취소) — 승인 결제의 남은 금액 전액을 환불한다. 접수된 고객 요청이 있으면 그 사유를 쓰므로 사유를 비워도
 * 되고, 그 밖에는 사유 1~500자가 필요하다. 결과(완료·확인 중)는 대화상자 안에 안내한다.
 */
export function OrderRefundDialog({
  orderId,
  hasRequest,
  supplyFailure = false,
  onSubmit,
  onSupplySubmit,
  onReload,
  onClose,
  pending,
  paymentId,
}: OrderRefundDialogProps) {
  const id = useId();
  const [reason, setReason] = useState('');
  return (
    <OrderActionDialog
      title={supplyFailure ? '공급 불가 취소' : '취소·환불'}
      submitLabel="환불 실행"
      onReload={onReload}
      onClose={onClose}
      pending={pending}
      validate={() =>
        (supplyFailure || !hasRequest || reason.length > 0) &&
        !orderReasonSchema.safeParse(reason).success
          ? '사유 1~500자를 입력해 주세요.'
          : null
      }
      onSubmit={async () =>
        refundResultMessage(
          supplyFailure
            ? await onSupplySubmit({ orderId, reason: reason.trim() })
            : await onSubmit({ orderId, ...(reason.trim() ? { reason: reason.trim() } : {}) }),
        )
      }
    >
      <p className={formStyles.notice}>승인 결제의 남은 금액 전액을 환불합니다.</p>
      <div className={formStyles.field}>
        <Label htmlFor={`${id}-reason`}>사유</Label>
        <textarea
          id={`${id}-reason`}
          className={formStyles.textarea}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {hasRequest && !supplyFailure && (
          <p className={formStyles.hint}>접수된 취소 요청의 사유를 사용합니다.</p>
        )}
      </div>
      {paymentId && (
        <Link
          to={`/payments/${paymentId}`}
          className="self-start text-sm font-medium underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
        >
          결제 상세에서 확인
        </Link>
      )}
    </OrderActionDialog>
  );
}
